/**
 * Renderer lifecycle (architecture §2.2 step 5, §11; ui-ux §6.2, §6.26):
 * - `init()` decides between the live renderer and lite mode «Без миниатюр» (`gfx.lite`, session-only): `?lite=1` /
 *   `render=none` (manual, no banner), `?nowebgpu=1` (simulated E1), `?simulate=no-adapter|device-failed|insecure`,
 *   an insecure context, or `createRenderer` throwing `WebGpuUnavailableError` (E1–E4).
 * - Device lost (E6): the clock pauses and «Чернила пролились — перерисовываем…» shows. With F2's engine the GPU state
 *   is rebuilt in place and reported through `onRendererDeviceEvent` (`lost` → `restored`, or `fatal` = second loss
 *   within 60 s → lite mode); handles stay valid, so screens are not re-created. Any other renderer (or an engine that
 *   never answers) gets the app-level path: destroy, re-create, re-create every mounted screen on it, resume; a second
 *   loss within 60 s → lite. A `marg:gpu-lost` CustomEvent on the canvas also enters that path.
 */
import { FRAME_BREAK_CAP, type Clock, type QualityTier, type Renderer, type RendererOptions, type TFunction, type Viewport } from '../shared';
import { createRenderer, EngineRenderer, onRendererDeviceEvent, WebGpuUnavailableError, type DeviceEvent } from '../engine';
import type { BootParams } from '../debug';
import { showLiteBanner, showStageNotice, type LiteReason } from './fallback';
import type { KeyValueStorage } from './save';
import type { SettingsStore } from './settings';

export const LOST_WINDOW_MS = 60_000;
export const RECOVER_TIMEOUT_MS = 3000;

export interface GfxDeps {
  canvas: HTMLCanvasElement;
  params: BootParams;
  settings: SettingsStore;
  clock: Clock;
  overlay: HTMLElement;
  t: TFunction;
  storage: KeyValueStorage | null;
  record(message: string): void;
  /** Re-creates every mounted screen on the current renderer (or on lite plates). */
  remount(reason: string): void;
  viewport(): Viewport;
}

const timeout = <T>(p: Promise<T>, ms: number): Promise<T> =>
  Promise.race([p, new Promise<T>((_, rej) => setTimeout(() => rej(new Error(`timed out after ${ms} ms`)), ms))]);

export class Gfx {
  renderer: Renderer | null = null;
  lite = false;
  liteReason: LiteReason | null = null;
  liteCode: string | undefined;
  /** Device losses (wall ms) inside the last 60 s. */
  private losses: number[] = [];
  /** Recovered losses this session (`__marg.getState()`). */
  recovered = 0;
  private recovering = false;
  private offDevice: (() => void) | null = null;
  /** An engine-reported loss in progress: the E6 notice and the clock state to restore. */
  private engineLoss: { notice: ReturnType<typeof showStageNotice>; wasPaused: boolean; timer: ReturnType<typeof setTimeout> } | null = null;

  constructor(private readonly d: GfxDeps) {
    d.settings.signal.subscribe(() => {
      if (this.renderer) this.applyPresentationSettings(this.renderer);
    });
    d.canvas.addEventListener('marg:gpu-lost', (e) => {
      const det = (e as CustomEvent<{ message?: string; recovering?: boolean }>).detail ?? {};
      void this.handleLost(det.message ?? 'device lost', det.recovering === true);
    });
  }

  /** Boot step 5: live renderer or lite mode. Never throws. */
  async init(): Promise<void> {
    const p = this.d.params;
    if (p.lite || this.d.settings.value.lite) { this.enterLite('manual'); return; }
    if (p.nowebgpu) { this.enterLite('no-api'); return; }
    if (p.simulate === 'no-adapter') { this.enterLite('no-adapter'); return; }
    if (p.simulate === 'device-failed') { this.enterLite('no-device', 'simulated'); return; }
    if (p.simulate === 'insecure' || (typeof isSecureContext === 'boolean' && !isSecureContext)) { this.enterLite('insecure'); return; }
    try {
      this.renderer = await this.create();
      this.setCanvasVisible(true);
    } catch (e) {
      this.renderer = null;
      if (e instanceof WebGpuUnavailableError) {
        const reason: LiteReason = e.reason === 'no-api' ? 'no-api' : e.reason === 'no-adapter' ? 'no-adapter' : e.reason === 'no-device' ? 'no-device' : 'pipeline';
        // Expected path (E1–E4 → lite mode): reported in `platform().liteReason`, not as an error.
        console.info(`[gfx] WebGPU unavailable (${e.reason}): ${e.message} → «Без миниатюр»`);
        this.enterLite(reason, e.reason === 'no-api' ? undefined : e.message);
      } else {
        this.d.record(`gpu: createRenderer failed: ${(e as Error)?.message ?? String(e)}`);
        this.enterLite('pipeline', (e as Error)?.message);
      }
    }
  }

  /** Shows the one-time banner when lite mode was entered automatically (not for `?lite=1`). */
  showBannerIfNeeded(): void {
    if (!this.lite || this.liteReason === 'manual' || this.liteReason === null) return;
    showLiteBanner(this.d.overlay, {
      reason: this.liteReason, code: this.liteCode, t: this.d.t, storage: this.d.storage, force: this.d.params.nowebgpu,
      retry: () => this.retry(),
    });
  }

  private async create(): Promise<Renderer> {
    const s = this.d.settings.value;
    const opts: RendererOptions & { renderScale?: number; msaa?: 1 | 4 } = {
      canvas: this.d.canvas,
      quality: s.quality,
      reducedMotion: s.motion !== 'full',
      flashes: s.flash,
      frameBreakCap: FRAME_BREAK_CAP,
    };
    if (this.d.params.renderScale !== null) opts.renderScale = this.d.params.renderScale;
    if (this.d.params.msaa !== null) opts.msaa = this.d.params.msaa;
    const r = await createRenderer(opts);
    this.applyPresentationSettings(r);
    r.resize(this.d.viewport());
    this.offDevice?.();
    this.offDevice = onRendererDeviceEvent(r, (e) => this.onDeviceEvent(e));
    return r;
  }

  private applyPresentationSettings(renderer: Renderer): void {
    if (!(renderer instanceof EngineRenderer)) return;
    const settings = this.d.settings.value;
    renderer.setReducedMotion(settings.motion !== 'full');
    renderer.setFlashes(settings.flash);
  }

  /**
   * F2's engine rebuilds its GPU state in place (handles stay valid) and reports it: `lost` → pause + E6 notice;
   * `restored` → resume (no remount); `fatal` (second loss within 60 s, or the rebuild failed) → lite mode.
   */
  private onDeviceEvent(e: DeviceEvent): void {
    const clock = this.d.clock;
    if (e.kind === 'error') { this.d.record(`gpu: ${e.message}`); return; }
    if (e.kind === 'lost') {
      this.d.record(`gpu: device lost (${e.message})`);
      if (this.engineLoss) return;
      const wasPaused = clock.paused;
      clock.paused = true;
      const notice = showStageNotice(this.d.overlay, this.d.t);
      // Safety net: no `restored`/`fatal` within the budget → the app re-creates the renderer itself.
      const timer = setTimeout(() => { this.finishEngineLoss(); void this.handleLost('engine did not recover'); }, RECOVER_TIMEOUT_MS + 2000);
      this.engineLoss = { notice, wasPaused, timer };
      return;
    }
    const loss = this.finishEngineLoss();
    if (e.kind === 'restored') {
      this.recovered++;
      this.d.record(`gpu: device lost — recovered in ${Math.round(e.ms)} ms`);
      if (loss) clock.paused = loss.wasPaused;
      return;
    }
    // fatal
    this.d.record(`gpu: ${e.message} → «Без миниатюр»`);
    try { this.renderer?.destroy(); } catch { /* gone */ }
    this.renderer = null;
    this.enterLite('lost');
    this.d.remount('device-lost-lite');
    if (loss) clock.paused = loss.wasPaused;
    this.showBannerIfNeeded();
  }

  private finishEngineLoss(): { wasPaused: boolean } | null {
    const l = this.engineLoss;
    if (!l) return null;
    clearTimeout(l.timer);
    l.notice.close();
    this.engineLoss = null;
    return { wasPaused: l.wasPaused };
  }

  /** Settings «Без миниатюр» on: drop the renderer and re-create the screens on plates. */
  goLite(): void {
    if (this.lite) return;
    this.offDevice?.();
    this.offDevice = null;
    try { this.renderer?.destroy(); } catch { /* ignore */ }
    this.renderer = null;
    this.enterLite('manual');
    this.d.remount('settings-lite');
  }

  enterLite(reason: LiteReason, code?: string): void {
    this.lite = true;
    this.liteReason = reason;
    this.liteCode = code;
    document.documentElement.dataset.lite = '1';
    this.setCanvasVisible(false);
  }

  private setCanvasVisible(on: boolean): void {
    this.d.canvas.style.visibility = on ? '' : 'hidden';
    if (on) delete document.documentElement.dataset.lite;
  }

  /** «Попробовать снова»: re-runs the GPU step without a reload. On success the stage goes live (screens re-created). */
  async retry(): Promise<boolean> {
    if (!this.lite) return true;
    try {
      this.renderer = await timeout(this.create(), 15_000);
    } catch (e) {
      this.d.record(`gpu: retry failed: ${(e as Error)?.message ?? String(e)}`);
      return false;
    }
    this.lite = false;
    this.liteReason = null;
    this.setCanvasVisible(true);
    this.d.remount('gpu-retry');
    return true;
  }

  /** E6: pause, notice, re-create (or wait for the engine's own recovery), resume; second loss in 60 s → lite. */
  async handleLost(message: string, engineRecovering = false): Promise<void> {
    if (this.recovering || this.lite) return;
    this.recovering = true;
    const clock = this.d.clock;
    const wasPaused = clock.paused;
    clock.paused = true;
    const now = Date.now();
    this.losses = this.losses.filter((t) => now - t < LOST_WINDOW_MS);
    this.losses.push(now);
    this.d.record(`gpu: device lost (${message})`);
    const notice = showStageNotice(this.d.overlay, this.d.t);
    const toLite = (): void => {
      try { this.renderer?.destroy(); } catch { /* already gone */ }
      this.renderer = null;
      this.enterLite('lost');
      this.d.remount('device-lost-lite');
      clock.paused = wasPaused;
      this.showBannerIfNeeded();
    };
    try {
      if (this.losses.length >= 2) { notice.close(); toLite(); return; }
      if (engineRecovering) {
        const ok = await new Promise<boolean>((res) => {
          const tm = setTimeout(() => res(false), RECOVER_TIMEOUT_MS);
          this.d.canvas.addEventListener('marg:gpu-restored', () => { clearTimeout(tm); res(true); }, { once: true });
        });
        if (ok) {
          this.recovered++;
          this.d.record('gpu: device lost — recovered by the engine');
          notice.close();
          clock.paused = wasPaused;
          return;
        }
      }
      try { this.renderer?.destroy(); } catch { /* the device is gone */ }
      this.offDevice?.();
      this.offDevice = null;
      this.renderer = null;
      try {
        this.renderer = await timeout(this.create(), RECOVER_TIMEOUT_MS);
      } catch (e) {
        this.d.record(`gpu: recovery failed: ${(e as Error)?.message ?? String(e)}`);
        notice.fail(() => location.reload(), toLite);
        return;
      }
      this.recovered++;
      this.d.record('gpu: device lost — recovered');
      this.d.remount('device-lost');
      notice.close();
      clock.paused = wasPaused;
    } finally {
      this.recovering = false;
    }
  }

  /** `__marg.loseDevice()`: the debug path of AT 12 (the renderer is destroyed and recovered by the app). */
  loseDevice(): void {
    const r = this.renderer as (Renderer & { simulateDeviceLoss?: () => Promise<void> }) | null;
    if (!r) { console.warn('[gfx] loseDevice: no renderer (lite mode)'); return; }
    if (typeof r.simulateDeviceLoss === 'function') { void r.simulateDeviceLoss(); return; }   // F2: real device.destroy() + events
    void this.handleLost('debug: loseDevice()');
  }

  /** `__marg.setQuality` / Settings «Качество». Persists the setting; re-creates the renderer unless it can switch live. */
  async setQuality(tier: QualityTier): Promise<void> {
    this.d.settings.set({ quality: tier });
    const r = this.renderer as (Renderer & { setQuality?: (q: QualityTier) => void | Promise<void> }) | null;
    if (!r) return;
    if (typeof r.setQuality === 'function') { await r.setQuality(tier); return; }
    try { r.destroy(); } catch { /* ignore */ }
    this.renderer = null;
    try {
      this.renderer = await this.create();
    } catch (e) {
      this.d.record(`gpu: quality change failed: ${(e as Error)?.message ?? String(e)}`);
      this.enterLite('pipeline', (e as Error)?.message);
    }
    this.d.remount('quality');
  }
}
